import React, { useState } from 'react';
import { Box, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import { useQAssureAuth } from '../services/qassureAuth';

/**
 * Floating QAssure AI panel.
 *
 * Hosts the standalone QAssureAI chat in an iframe so the same UI is
 * available everywhere in the portal without re-implementing the chat
 * protocol. Mounts a small launcher button bottom-right; clicking it
 * toggles a fixed panel containing the iframe.
 *
 * Hidden on the `/qassure-ai` route since that page already shows the
 * chat full-screen.
 */
const QAssureChatFloating = () => {
  const [open, setOpen] = useState(false);
  const { token, qassureUrl, error, loading } = useQAssureAuth();

  if (typeof window !== 'undefined' && window.location.pathname === '/qassure-ai') {
    return null;
  }

  const launcher = (
    <Tooltip title="Open QAssure AI" placement="left">
      <IconButton
        onClick={() => setOpen((v) => !v)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 56,
          height: 56,
          bgcolor: 'primary.main',
          color: 'common.white',
          boxShadow: 6,
          zIndex: 1300,
          '&:hover': { bgcolor: 'primary.dark' },
        }}
        aria-label="Open QAssure AI"
      >
        <ChatIcon />
      </IconButton>
    </Tooltip>
  );

  if (!open) return launcher;

  const src = token && qassureUrl
    ? `${qassureUrl}${qassureUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`
    : null;

  return (
    <>
      {launcher}
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 96,
          right: 24,
          width: { xs: 'calc(100vw - 48px)', sm: 420 },
          height: { xs: 'calc(100vh - 160px)', sm: 600 },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 1300,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            bgcolor: 'primary.main',
            color: 'common.white',
          }}
        >
          <Typography variant="subtitle2">QAssure AI</Typography>
          <IconButton size="small" sx={{ color: 'common.white' }} onClick={() => setOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, position: 'relative' }}>
          {loading && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Connecting…
              </Typography>
            </Box>
          )}
          {error && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="error">
                Could not authenticate with QAssure AI.
              </Typography>
            </Box>
          )}
          {src && (
            <iframe
              src={src}
              title="QAssure AI"
              style={{ width: '100%', height: '100%', border: 0 }}
              allow="clipboard-read; clipboard-write"
            />
          )}
        </Box>
      </Paper>
    </>
  );
};

export default QAssureChatFloating;
